import React, { memo, useEffect, useRef, useState } from 'react';
import DeliveryDashboardSideCard from '../DeliveryDashboardSideCard';
import PIEText from '@src/shared/components/PIEText';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import PIESegmentedControl, {
  IPIESegmentedControlOption,
} from '@src/shared/components/PIESegmentedControl';
import { useTranslation } from 'react-i18next';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import DeliveryDashboardBottomSheet from '../DeliveryDashboardBottomSheet';
import DashboardCourierList from '../DashboardCourierList';
import DashboardOrderList from '../DashboardOrderList';
import { DeliveryDashboardScreens } from '../../models/model-enum';
import { useShallow } from 'zustand/react/shallow';
import { DETAILED_ZOOM_LEVEL } from '../../models/model-constants';
import { useDashboardUIStore } from '../../store/dashboardUIStore';
import { useDashboardMapStore } from '../../store/dashboardMapStore';
import useDeliveryDashboadAnalytics from '../../tracking/use-delivery-dashboard-analytics';
import DeliveryDashboardMapFunctions from '../DeliveryDashboardMapFunctions';
import { isPWA } from '@src/shared/utils';

export enum ManageDeliverySegmentedControlSelection {
  COURIERS = 'couriers',
  ORDERS = 'orders',
}

const DashboardManageDelivery = () => {
  const { isResolutionMobile } = useDeviceDetect();
  const [togglerRef, setTogglerRef] = useState();
  const { isIOS } = useDeviceDetect();
  const { t } = useTranslation();
  const [setMapReFocusToCenter] = useDashboardMapStore(
    useShallow((state) => [state.setMapReFocusToCenter])
  );

  /* Component State */

  const {
    setActiveOrderHash,
    setActiveScreen,
    setActiveManageDeliveryTab,
    setActiveCourierId,
    activeManageDeliveryTab,
  } = useDashboardUIStore();

  const { trackOrderDetailView } = useDeliveryDashboadAnalytics();

  const { trackCourierDetailView } = useDeliveryDashboadAnalytics();

  const [setMapCenter, setMapPanOffset, setMapZoomLevel] = useDashboardMapStore(
    useShallow((state) => [state.setMapCenter, state.setMapPanOffset, state.setMapZoomLevel])
  );

  const handlerTogglerRef = (ref: any) => {
    const { refs } = ref;
    setTogglerRef(refs);
  };

  const [segmentedControlValue, setSegmentedControlValue] =
    useState<ManageDeliverySegmentedControlSelection>(activeManageDeliveryTab);

  useEffect(() => {
    setActiveManageDeliveryTab(segmentedControlValue);
  }, [segmentedControlValue]);

  useEffect(() => {
    setSegmentedControlValue(activeManageDeliveryTab);
  }, [activeManageDeliveryTab]);

  const bottomSheetRef = useRef<any>(null);

  const SEGMENTED_CONTROL_OPTIONS: Array<IPIESegmentedControlOption> = [
    {
      label: t('dashboard.segmented_COURIERS'),
      value: ManageDeliverySegmentedControlSelection.COURIERS,
    },
    {
      label: t('dashboard.segmented_ORDERS'),
      value: ManageDeliverySegmentedControlSelection.ORDERS,
    },
  ];

  const renderSelectedScreen = () => {
    switch (segmentedControlValue) {
      case ManageDeliverySegmentedControlSelection.COURIERS:
        return (
          <>
            <DashboardCourierList
              onCourierSelected={(courier) => {
                trackCourierDetailView(courier.id);
                bottomSheetRef.current?.snapTo(0);
                window.requestAnimationFrame(() => {
                  setActiveCourierId(courier.id);
                  setActiveScreen(DeliveryDashboardScreens.COURIER_DETAIL_SCREEN);
                });
              }}
            />
          </>
        );
      case ManageDeliverySegmentedControlSelection.ORDERS:
        return (
          <DashboardOrderList
            togglerRefs={togglerRef}
            onOrderSelected={(order) => {
              trackOrderDetailView(order.hash, order.uuid);
              bottomSheetRef.current?.snapTo(0);
              window.requestAnimationFrame(() => {
                setActiveOrderHash(order.hash);
                setActiveScreen(DeliveryDashboardScreens.ORDER_DETAIL_SCREEN);
                setMapCenter({
                  lat: order.location.latitude,
                  lng: order.location.longitude,
                });
                setMapZoomLevel(DETAILED_ZOOM_LEVEL);
                isResolutionMobile ? setMapPanOffset(70) : null;
              });
            }}
          />
        );
    }
  };

  const renderContent = () => {
    return (
      <PIEComponentUtil
        paddingTop="var(--xds-spacing-d)"
        paddingX={isResolutionMobile ? 'var(--xds-spacing-d)' : ''}
      >
        <div id="manage-delivery-segmented-control-container">
          <PIESegmentedControl
            name="manage-delivery-segmented-control"
            options={SEGMENTED_CONTROL_OPTIONS}
            value={segmentedControlValue}
            onChange={(value: ManageDeliverySegmentedControlSelection) =>
              setSegmentedControlValue(value)
            }
            modFluid={true}
            qaSelector="manage-delivery-segmented-control"
          />
        </div>
        {renderSelectedScreen()}
      </PIEComponentUtil>
    );
  };

  return (
    <>
      {!isResolutionMobile ? (
        <DeliveryDashboardSideCard
          onTogglerRef={handlerTogglerRef}
          header={
            <PIEText
              size="var(--xds-font-size-20)"
              align="left"
              text={t('dashboard.toggler_TITLE')}
              fontWeight="var(--xds-font-weight-bold)"
            ></PIEText>
          }
          showBackButton={false}
        >
          {renderContent()}
        </DeliveryDashboardSideCard>
      ) : (
        <DeliveryDashboardBottomSheet
          onBottomSheetHeight={() => {}}
          onRefInitialized={(ref) => (bottomSheetRef.current = ref.current)}
          header={
            <PIEText
              size="var(--xds-font-size-20)"
              align="left"
              text={t('dashboard.toggler_TITLE')}
              fontWeight="var(--xds-font-weight-bold)"
            ></PIEText>
          }
          sibling={
            <DeliveryDashboardMapFunctions
              onCenterClick={() => {
                setMapReFocusToCenter(true);
              }}
              iconPosition={isPWA() && isIOS ? '100px' : '116px'}
            />
          }
        >
          {renderContent()}
        </DeliveryDashboardBottomSheet>
      )}
    </>
  );
};

export default memo(DashboardManageDelivery);
